// The box and radius tools.
//
// Google removed DrawingManager in Maps JS 3.65, so the drag is driven from the
// map's own mouse events. Two things make that work that did not before:
//
//   - while it is being dragged the shape is clickable:false, so it does not
//     sit over the cursor and swallow the mousemove and mouseup the map needs.
//     That is what left a box stuck mid-draw and a circle unable to shrink.
//   - mouseup is also watched on the window, so releasing the button off the
//     map still ends the drag.
//
// Once drawn the shape becomes editable and draggable, which is where the
// design's corner handles and radius handle come from.

const METRES_PER_MILE = 1609.34;
const METRES_PER_LAT_DEGREE = 110540;
const EQUATOR_METRES_PER_LNG_DEGREE = 111320;

// A drag shorter than this is a click, and a click places a shape at the size
// the panel is set to rather than a zero-sized one.
const MIN_DRAG_PX = 6;

// Handle drags fire continuously; the parcel lookup waits for them to settle.
const COMMIT_DELAY_MS = 350;

const STYLE = {
    fillColor: '#1570EF',
    fillOpacity: 0.1,
    strokeColor: '#7CB8FF',
    strokeWeight: 2,
    zIndex: 3,
};

export default class ShapeTool {
    constructor(map, { onChange, onCommit }) {
        this.map = map;
        this.onChange = onChange;
        this.onCommit = onCommit;

        this.mode = null;
        this.shape = null;
        this.origin = null;
        this.originPixel = null;
        this.size = { widthMiles: 0.4, heightMiles: 0.25, radiusMiles: 0.25 };
        this.commitTimer = null;

        this.endDrag = () => this.finishDrag();
        window.addEventListener('mouseup', this.endDrag);

        this.listeners = [
            map.addListener('mousedown', (event) => this.beginDrag(event)),
            map.addListener('mousemove', (event) => this.resizeDrag(event)),
            map.addListener('mouseup', () => this.finishDrag()),
        ];
    }

    destroy() {
        window.removeEventListener('mouseup', this.endDrag);
        clearTimeout(this.commitTimer);
        this.listeners.forEach((listener) => listener.remove());
        this.clear();
    }

    /** Arm a tool, or disarm it when it is already the armed one. */
    toggle(mode) {
        this.arm(this.mode === mode ? null : mode);
        return this.mode;
    }

    arm(mode) {
        this.mode = mode;
        this.map.setOptions({
            draggable: !mode,
            draggableCursor: mode ? 'crosshair' : null,
        });
    }

    clear() {
        clearTimeout(this.commitTimer);

        if (this.shape) this.shape.setMap(null);

        this.shape = null;
        this.origin = null;
        this.arm(null);
    }

    hasShape() {
        return this.shape !== null;
    }

    /** Tested on getRadius, not getBounds — a Circle answers getBounds too. */
    isBox() {
        return this.shape !== null && typeof this.shape.getRadius !== 'function';
    }

    /**
     * Whether a point is inside the drawn shape.
     *
     * A circle is measured, not tested against its bounds: the bounds of a
     * circle are its bounding square, so the four corners outside it would
     * count as inside and a click there would not dismiss the shape.
     */
    containsPoint(latLng) {
        if (this.shape === null) return false;

        if (this.isBox()) return this.shape.getBounds().contains(latLng);

        return metresBetween(this.shape.getCenter(), latLng) <= this.shape.getRadius();
    }

    // ------------------------------------------------------------- dragging

    beginDrag(event) {
        if (!this.mode) return;

        if (this.shape) this.shape.setMap(null);

        this.origin = event.latLng;
        this.originPixel = { x: event.domEvent.clientX, y: event.domEvent.clientY };

        // clickable:false is the whole trick — the shape must not stand between
        // the pointer and the map while the pointer is still down.
        const options = { ...STYLE, map: this.map, clickable: false };

        this.shape = this.mode === 'radius'
            ? new google.maps.Circle({ ...options, center: event.latLng, radius: 0 })
            : new google.maps.Rectangle({ ...options, bounds: new google.maps.LatLngBounds(event.latLng, event.latLng) });
    }

    resizeDrag(event) {
        if (!this.origin || !this.shape) return;

        if (this.mode === 'radius') {
            this.shape.setRadius(metresBetween(this.origin, event.latLng));
        } else {
            this.shape.setBounds(new google.maps.LatLngBounds(this.origin, this.origin).extend(event.latLng));
        }

        this.publish();
    }

    finishDrag(event) {
        if (!this.origin || !this.shape) return;

        const dragged = this.draggedFar(event);
        const centre = this.origin;

        this.origin = null;

        if (!dragged) {
            // A plain click: place the shape at the size the panel is showing.
            if (this.mode === 'radius') {
                this.shape.setCenter(centre);
                this.shape.setRadius(this.size.radiusMiles * METRES_PER_MILE);
            } else {
                this.shape.setBounds(this.boundsAround(centre, this.size.widthMiles, this.size.heightMiles));
            }
        }

        this.shape.setOptions({ clickable: true, editable: true, draggable: true });
        this.watchShape();
        this.arm(null);
        this.publish();
        this.commit();
    }

    /**
     * Whether the pointer travelled far enough to count as a drag. Falls back
     * to the shape's own size when the release came from the window listener,
     * which carries no map coordinates.
     */
    draggedFar(event) {
        if (event && event.domEvent && this.originPixel) {
            return (
                Math.abs(event.domEvent.clientX - this.originPixel.x) > MIN_DRAG_PX ||
                Math.abs(event.domEvent.clientY - this.originPixel.y) > MIN_DRAG_PX
            );
        }

        return this.mode === 'radius'
            ? this.shape.getRadius() > 20
            : metresBetween(this.shape.getBounds().getSouthWest(), this.shape.getBounds().getNorthEast()) > 30;
    }

    watchShape() {
        const events = this.isBox() ? ['bounds_changed'] : ['radius_changed', 'center_changed'];

        events.forEach((name) =>
            this.shape.addListener(name, () => {
                this.publish();
                this.commit();
            })
        );
    }

    // ---------------------------------------------------------------- sizing

    /** Resize from the panel's mile inputs, keeping the centre where it is. */
    resizeTo(size) {
        this.size = { ...this.size, ...size };

        if (!this.shape) return;

        if (this.isBox()) {
            this.shape.setBounds(this.boundsAround(this.shape.getBounds().getCenter(), this.size.widthMiles, this.size.heightMiles));
        } else {
            this.shape.setRadius(this.size.radiusMiles * METRES_PER_MILE);
        }

        this.publish();
        this.commit();
    }

    boundsAround(centre, widthMiles, heightMiles) {
        const halfLat = (heightMiles * METRES_PER_MILE) / 2 / METRES_PER_LAT_DEGREE;
        const halfLng = (widthMiles * METRES_PER_MILE) / 2 / metresPerLngDegree(centre.lat());

        return new google.maps.LatLngBounds(
            { lat: centre.lat() - halfLat, lng: centre.lng() - halfLng },
            { lat: centre.lat() + halfLat, lng: centre.lng() + halfLng }
        );
    }

    /** The current size in miles, for the panel's inputs. */
    measure() {
        if (!this.shape) return null;

        if (!this.isBox()) {
            return { type: 'radius', radiusMiles: round2(this.shape.getRadius() / METRES_PER_MILE) };
        }

        const bounds = this.shape.getBounds();
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const centreLat = (ne.lat() + sw.lat()) / 2;

        return {
            type: 'box',
            widthMiles: round2(((ne.lng() - sw.lng()) * metresPerLngDegree(centreLat)) / METRES_PER_MILE),
            heightMiles: round2(((ne.lat() - sw.lat()) * METRES_PER_LAT_DEGREE) / METRES_PER_MILE),
        };
    }

    /**
     * The shape as WKT, for the parcel lookup.
     *
     * A radius goes over as a polygon rather than a point: the parcel API
     * searches by geometry and has no radius argument, so a bare point would
     * only ever return the one parcel under the centre.
     */
    toWkt() {
        if (!this.shape) return null;

        if (!this.isBox()) {
            return circleToWkt(this.shape.getCenter(), this.shape.getRadius());
        }

        const bounds = this.shape.getBounds();
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const corners = [
            [sw.lng(), sw.lat()],
            [ne.lng(), sw.lat()],
            [ne.lng(), ne.lat()],
            [sw.lng(), ne.lat()],
            [sw.lng(), sw.lat()],
        ];

        return 'POLYGON((' + corners.map(([lng, lat]) => point(lng, lat)).join(', ') + '))';
    }

    publish() {
        const measured = this.measure();
        if (measured) {
            this.size = { ...this.size, ...measured };
            this.onChange(measured);
        }
    }

    commit() {
        clearTimeout(this.commitTimer);
        this.commitTimer = setTimeout(() => this.onCommit(), COMMIT_DELAY_MS);
    }
}

/**
 * One WKT coordinate.
 *
 * Six decimals is a tenth of a metre, and it is what makes a circle usable at
 * all: the lookup travels as a GET query string, and a 48-sided ring written at
 * full double precision is 1.8 KB of it — long enough that LightBox answered
 * every radius search with a 500. The same ring rounded here is 1.1 KB and
 * returns the same parcels.
 */
function point(lng, lat) {
    return lng.toFixed(6) + ' ' + lat.toFixed(6);
}

/** A circle as a 48-sided polygon — under half a metre out at a mile. */
function circleToWkt(centre, radiusMetres) {
    const latStep = radiusMetres / METRES_PER_LAT_DEGREE;
    const lngStep = radiusMetres / metresPerLngDegree(centre.lat());
    const points = [];

    for (let i = 0; i <= 48; i++) {
        const angle = (i / 48) * 2 * Math.PI;
        points.push(point(
            centre.lng() + lngStep * Math.cos(angle),
            centre.lat() + latStep * Math.sin(angle)
        ));
    }

    return 'POLYGON((' + points.join(', ') + '))';
}

function metresPerLngDegree(latitude) {
    return EQUATOR_METRES_PER_LNG_DEGREE * Math.cos((latitude * Math.PI) / 180);
}

function metresBetween(from, to) {
    const radius = 6378137;
    const toRad = (degrees) => (degrees * Math.PI) / 180;
    const dLat = toRad(to.lat() - from.lat());
    const dLng = toRad(to.lng() - from.lng());
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(from.lat())) * Math.cos(toRad(to.lat())) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

    return 2 * radius * Math.asin(Math.sqrt(a));
}

function round2(value) {
    return Math.round(value * 100) / 100;
}
